/**
 * ROUND8: how many dog teams a mission carries (`MissionParams.dogTeams`; the generator places them,
 * src/gen/dogTeams.ts). Native teams come with the threat rating - one at 6-7 and two at 8-9 on
 * Standard, a threat step sooner for each difficulty detent above it and one later on Milk Run - and the
 * Dog Patrols card adds one more on top. Operations follow their own difficulty; Practice and the Daily
 * Drop follow the rating at Standard; Classic flies the 1987 garrison, which had no dogs (01 §7).
 *
 * The count is stamped wherever the rating is: when a node is dressed, whenever the map refreshes its
 * ratings, and at deployment, so the card, the briefing and the map the ranger lands on all agree.
 */

import { clamp } from '../sim/math';
import type { MissionParams, ModifierId } from '../sim/types';
import { DOG_TEAMS } from './content/dogs';
import type { Operation } from './types';

/** The difficulty Practice and the Daily Drop are rated at. */
export const STANDARD = DOG_TEAMS.standard;

/** Teams for a mission rated `threat` (1-9) on difficulty `difficulty` (0-3), with these modifiers. */
export function dogTeamsFor(threat: number, difficulty: number, modifiers: readonly ModifierId[]): number {
  const shift = clamp(Math.round(difficulty), 0, 3) - DOG_TEAMS.standard;
  const rating = clamp(Math.round(threat), 1, 9);
  const native = rating >= DOG_TEAMS.twoFrom - shift ? 2 : rating >= DOG_TEAMS.oneFrom - shift ? 1 : 0;
  return native + (modifiers.includes('dog-patrols') ? DOG_TEAMS.modifierTeams : 0);
}

/** Write the count onto a mission's parameters: present only when there are dogs, so a dog-free one is unchanged. */
export function stampDogTeams(params: MissionParams, difficulty: number): void {
  const teams = dogTeamsFor(params.threat, difficulty, params.modifiers);
  if (teams > 0) params.dogTeams = teams;
  else delete params.dogTeams;
}

/** Every node of an operation, at its current rating (a save written before ROUND8 has no counts on it). */
export function stampOperationDogs(op: Pick<Operation, 'difficulty' | 'nodes'>): void {
  for (const node of op.nodes) {
    // A save is only as sound as the checks `parseProfile` makes of it: read each node warily.
    const p = (node as Partial<typeof node> | null)?.params;
    if (p && typeof p.threat === 'number' && Array.isArray(p.modifiers)) stampDogTeams(p, op.difficulty);
  }
}

/** Practice's parameters as flown: its dog teams follow the rating at Standard, whatever it was handed. */
export function practiceParams(params: MissionParams): MissionParams {
  const out: MissionParams = { ...params, modifiers: [...params.modifiers], intel: [...params.intel] };
  stampDogTeams(out, STANDARD);
  return out;
}

/** Classic is the 1987 game: no dogs, whatever the parameters say, and no card that promises one. */
export function withoutDogs(params: MissionParams): MissionParams {
  const out: MissionParams = { ...params, modifiers: params.modifiers.filter((m) => m !== 'dog-patrols') };
  delete out.dogTeams;
  return out;
}
